import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import OxyHeader from "../components/OxyHeader";

const ICON_MAP = {
  "shopping-bag": Icons.ShoppingBag, "briefcase": Icons.Briefcase, "newspaper": Icons.Newspaper,
  "pen-tool": Icons.PenTool, "image": Icons.Image, "utensils": Icons.Utensils,
  "graduation-cap": Icons.GraduationCap, "layers": Icons.Layers, "heart": Icons.Heart,
  "user": Icons.User, "wrench": Icons.Wrench, "rocket": Icons.Rocket,
};

export default function CreateWebsite() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preSelectedType = params.get("type");
  const [step, setStep] = useState(preSelectedType ? 2 : 1);
  const [types, setTypes] = useState([]);
  const [themes, setThemes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [name, setName] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiWorking, setAiWorking] = useState(false);
  const [aiOutput, setAiOutput] = useState("");

  useEffect(() => { if (!user) navigate("/"); }, [user]);
  useEffect(() => {
    api.get("/oxy/website-types").then(r => {
      setTypes(r.data);
      if (preSelectedType) {
        const t = r.data.find(x => x.id === preSelectedType);
        if (t) { setSelectedType(t); setStep(2); }
      }
    });
  }, []);
  useEffect(() => {
    if (selectedType) api.get(`/oxy/themes?type=${selectedType.id}`).then(r => setThemes(r.data.themes || []));
  }, [selectedType]);

  const create = async () => {
    if (!name.trim() || !selectedType) return toast.error("Fill in the name");
    try {
      const r = await api.post("/websites", {
        type: selectedType.id,
        name,
        theme_id: selectedTheme?.id || "",
        language: "bn",
      });
      toast.success("Website created!");
      navigate(`/websites/${r.data.website_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const aiGenerate = async () => {
    if (!aiPrompt.trim()) return toast.error("Describe your website");
    setAiWorking(true);
    try {
      const r = await api.post("/ai/generate", {
        task: "outline",
        prompt: aiPrompt,
        context: `Website type: ${selectedType?.id}. Language: Bengali/English mixed.`,
      });
      setAiOutput(r.data.output);
      toast.success("AI generated structure!");
    } catch { toast.error("AI failed"); } finally { setAiWorking(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <OxyHeader/>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
            <span className={step >= 1 ? "text-sky-600 font-bold" : ""}>1. Type</span>
            <span>→</span>
            <span className={step >= 2 ? "text-sky-600 font-bold" : ""}>2. Theme</span>
            <span>→</span>
            <span className={step >= 3 ? "text-sky-600 font-bold" : ""}>3. Details</span>
            <span>→</span>
            <span className={step >= 4 ? "text-sky-600 font-bold" : ""}>4. AI (optional)</span>
          </div>
          <h1 className="text-3xl font-extrabold">Create your website</h1>
        </div>

        {/* Step 1: Type */}
        {step === 1 && (
          <div>
            <h2 className="font-bold mb-4">Choose website type</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {types.map(t => {
                const Icon = ICON_MAP[t.icon] || Icons.Globe;
                return (
                  <button
                    key={t.id}
                    onClick={() => { setSelectedType(t); setStep(2); }}
                    data-testid={`type-${t.id}`}
                    className="text-left p-5 bg-white rounded-2xl border border-slate-200 hover:border-sky-400 hover:shadow-lg transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center mb-3"><Icon className="w-5 h-5"/></div>
                    <div className="font-bold">{t.name}</div>
                    <div className="text-xs text-slate-500 font-bn">{t.name_bn}</div>
                    <div className="text-xs text-slate-400 mt-1">{t.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Theme */}
        {step === 2 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold">Pick a theme</h2>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>← Back</Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {themes.map(th => (
                <button
                  key={th.id}
                  onClick={() => { setSelectedTheme(th); setStep(3); }}
                  data-testid={`theme-${th.id}`}
                  className={`text-left rounded-2xl overflow-hidden border-2 transition-all ${selectedTheme?.id === th.id ? "border-sky-500" : "border-slate-200 hover:border-sky-300"} bg-white`}
                >
                  <img src={th.preview} className="w-full h-40 object-cover" alt=""/>
                  <div className="p-3">
                    <div className="font-bold">{th.name}</div>
                    <div className="text-xs text-slate-400">{th.id}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Details */}
        {step === 3 && (
          <div className="bg-white p-6 rounded-2xl border border-slate-200 max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold">Website details</h2>
              <Button variant="ghost" size="sm" onClick={() => setStep(2)}>← Back</Button>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Website name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. ABC Fashion" data-testid="website-name-input"/>
              </div>
              <div className="p-3 rounded-lg bg-sky-50 border border-sky-100 text-sm">
                <div className="font-semibold">Type: <span className="text-sky-700">{selectedType?.name}</span></div>
                <div className="text-xs text-slate-500 mt-0.5">Theme: {selectedTheme?.name || "default"}</div>
              </div>
              <div className="flex gap-2">
                <Button onClick={create} className="flex-1 bg-sky-500 hover:bg-sky-600" data-testid="create-website-submit">Create Website</Button>
                <Button onClick={() => setStep(4)} variant="outline" data-testid="try-ai-btn">🤖 Try AI</Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: AI */}
        {step === 4 && (
          <div className="bg-white p-6 rounded-2xl border border-slate-200 max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold">AI Website Assistant 🤖</h2>
              <Button variant="ghost" size="sm" onClick={() => setStep(3)}>← Back</Button>
            </div>
            <p className="text-sm text-slate-500 mb-3 font-bn">সংক্ষেপে বর্ণনা দিন — AI structure ও content বানিয়ে দেবে</p>
            <Textarea
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              rows={4}
              placeholder="e.g. আমি ঢাকায় একটি clothing brand চালাই। নাম ABC Fashion। মেয়েদের ফ্যাশন প্রোডাক্ট বিক্রি করি।"
              data-testid="ai-prompt"
            />
            <div className="mt-3 flex gap-2">
              <Button onClick={aiGenerate} disabled={aiWorking} className="bg-sky-500 hover:bg-sky-600" data-testid="ai-generate-btn">
                {aiWorking ? "Generating..." : "✨ Generate draft content"}
              </Button>
              <Button onClick={create} variant="outline" data-testid="skip-ai-create">Skip & Create</Button>
            </div>
            <div className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
              <span className="font-black">ℹ</span>
              <span className="font-bn"><b>AI শুধু কনটেন্ট লিখে দেয় — অটো Publish করে না।</b> আপনি রিভিউ করে নিজে Publish বাটন চাপবেন।</span>
            </div>
            {aiOutput && (
              <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200 max-h-64 overflow-y-auto text-sm whitespace-pre-wrap font-mono">
                {aiOutput}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
